import * as React from "react";
import { useDispatch } from "react-redux";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { useFormik, FormikHelpers as FormikActions } from "formik";

import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import LOGO from "src/assets/svgs/logo.png";

import { supportLogin } from "src/apiService/modules/support";
import { setSupportLoginToken } from "src/store/support/actions";
import PasswordField from "src/components/PasswordField";

interface LoginFormValues {
  username: string;
  password: string;
}

const useStyles = makeStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: theme.palette.common.white,
    },
  },
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    padding: 12,
    position: "relative",
  },
  loading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: "-12px",
    marginLeft: "-12px",
  },
  forgot: {
    marginTop: theme.spacing(1),
    textAlign: "center",
  },
}));

export const AccountantLoginPage = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const formik = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: async (
      values: LoginFormValues,
      { setStatus }: FormikActions<LoginFormValues>
    ) => {
      if (!values.username || !values.password) return;
      try {
        // Accountant accounts are stored with a normalized (lower-cased)
        // email as their username; match that so "Jane@Firm.com" signs in.
        const result = await supportLogin(
          values.username.trim().toLowerCase(),
          values.password
        );
        if (result.data.token) {
          if (result.data.role !== "accountant") {
            setStatus({ error: "Invalid email / password" });
            return;
          }
          dispatch(
            setSupportLoginToken(result.data.token, result.data.role ?? null)
          );
          history.push("/accountant");
        } else {
          setStatus({ error: "Invalid email / password" });
        }
      } catch (e) {
        setStatus({ error: "System error" });
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Box className="mb-5 h-36">
          <img src={LOGO} alt="My Reseller Genie" className="h-full" />
        </Box>
        <Typography component="h1" variant="h5">
          Accountant Sign in
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          Access the books your clients have shared with you.
        </Typography>
        <form
          className={classes.form}
          noValidate
          onSubmit={formik.handleSubmit}
        >
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Email"
            name="username"
            autoComplete="username"
            autoFocus
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.username}
            disabled={formik.isSubmitting}
          />
          <PasswordField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            id="password"
            autoComplete="current-password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            disabled={formik.isSubmitting}
          />
          {formik.status?.error && !formik.isSubmitting && (
            <Alert severity="error">{formik.status?.error}</Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={formik.isSubmitting}
              >
                Sign In
                {formik.isSubmitting && (
                  <CircularProgress
                    className={classes.loading}
                    size={24}
                    color="inherit"
                  />
                )}
              </Button>
            </Grid>
          </Grid>
          <div className={classes.forgot}>
            <Link
              component={RouterLink}
              to="/accountant/forgot-password"
              variant="body2"
            >
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default AccountantLoginPage;
